import { ref } from "vue";
import { authStore } from "@/store/auth.js";
import { cloudFunctions } from "@/store/cloudFunctions.js";

// The Storefront bridge proxy (StorefrontPlugin.swift), created lazily by
// registerPlugin inside the iOS-gated init below. registerPlugin (not
// window.Capacitor.Plugins) is what makes the native method callable from JS —
// the same pattern audioPlaybackService uses for NativeAudioPlayer. Kept lazy
// (dynamic import of @capacitor/core) so importing this module doesn't pull in
// @capacitor/core on web/in unit tests.
let _storefrontPlugin = null;

// US + EU27/EEA + GB: Stripe-eligible regions for the ANDROID app (and the
// upper bound for web). iOS uses IOS_EXTERNAL_LINK_COUNTRIES below instead.
const STRIPE_ELIGIBLE_COUNTRIES = new Set([
  "US",
  // EU27
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // EEA (non-EU)
  "IS",
  "LI",
  "NO",
  // UK
  "GB",
]);

// iOS: Apple's permission for external purchase links attaches to the App
// Store STOREFRONT, not the user's IP-detected country. US storefront apps
// need no entitlement or StoreKit API (Guideline 3.1.1(a)); the EU requires the
// external-link entitlement and forbids IAP alongside it, the UK allows no
// link-out yet, and Japan's MSCA path needs its own entitlement — so every
// non-US storefront stays iap-only. StoreKit reports the storefront as ISO
// 3166-1 ALPHA-3 (e.g. "USA"), so this set is alpha-3 (no alpha-3→alpha-2
// mapping needed for a one-entry set). See the 2026-07-15 account-deletion +
// iOS-storefront spec, Part 2.
const IOS_EXTERNAL_LINK_STOREFRONTS = new Set(["USA"]);

// The current iOS App Store storefront (alpha-3), resolved from StoreKit via the
// native Storefront plugin at launch. A Vue ref so computeds that call
// getPaymentStrategy re-evaluate when it resolves (nil at cold launch) or when
// the user switches their Apple ID region mid-session. null until resolved →
// getPaymentStrategy fails closed to iap-only. Module-scoped, so the test seam
// _setIosStorefrontCountryForTest drives the iOS branch in unit tests.
const iosStorefrontCountry = ref(null);

export const platformBilling = {
  isNativeApp() {
    return (
      typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.() === true
    );
  },

  getNativePlatform() {
    if (!this.isNativeApp()) return null;
    const platform = window.Capacitor.getPlatform?.();
    if (platform === "ios") return "ios";
    if (platform === "android") return "android";
    return "android"; // default for Capacitor
  },

  /**
   * Get user's country from RTDB data or fallback to navigator locale.
   * @param {Object} userData - The user's data from Firebase RTDB
   * @returns {string} ISO 3166-1 alpha-2 country code
   */
  getUserCountry(userData) {
    // Primary: stored country from backend detection
    const storedCountry = userData?.region?.country;
    if (storedCountry && storedCountry.length === 2) {
      return storedCountry.toUpperCase();
    }

    // Fallback: extract region from navigator.language (e.g. "en-US" -> "US")
    const locale = navigator.language || "";
    const parts = locale.split("-");
    if (parts.length >= 2) {
      const region = parts[parts.length - 1].toUpperCase();
      if (region.length === 2) return region;
    }

    return "US"; // Safe default
  },

  isStripeRegion(countryCode) {
    return STRIPE_ELIGIBLE_COUNTRIES.has(countryCode);
  },

  /**
   * Determine the payment strategy for the current user.
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code
   * @returns {"stripe-only"|"stripe-preferred"|"iap-only"}
   */
  getPaymentStrategy(countryCode) {
    if (!this.isNativeApp()) {
      return "stripe-only";
    }
    // iOS gating is by App Store storefront (StoreKit), NOT the IP-detected
    // countryCode, which no longer participates on iOS at all. Fail CLOSED:
    // an unresolved/unknown storefront (null at cold launch, no store account)
    // means no link-out. Reading iosStorefrontCountry.value here makes computeds
    // that call getPaymentStrategy reactive to it — the Stripe button appears
    // when the ref lands.
    if (this.getNativePlatform() === "ios") {
      return IOS_EXTERNAL_LINK_STOREFRONTS.has(iosStorefrontCountry.value)
        ? "stripe-preferred"
        : "iap-only";
    }
    // Normalize to uppercase: the eligibility sets hold ISO 3166-1 alpha-2
    // codes in uppercase, and a stray lowercase code would otherwise silently
    // downgrade an eligible user (e.g. a US Android user) to iap-only.
    const normalizedCountry =
      typeof countryCode === "string" ? countryCode.toUpperCase() : "";
    if (this.isStripeRegion(normalizedCountry)) {
      return "stripe-preferred";
    }
    return "iap-only";
  },

  /**
   * Call the detectRegion endpoint to save user's country server-side.
   * Should be called once after auth if country is not yet set.
   */
  async detectRegion() {
    try {
      const token = await authStore.getIdToken();
      if (!token) {
        console.warn("detectRegion: no auth token, skipping");
        return null;
      }
      const response = await fetch(cloudFunctions.getDetectRegionApi(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          localeHint: navigator.language || "",
        }),
      });

      if (!response.ok) {
        console.error("detectRegion failed:", response.status);
        return null;
      }

      const data = await response.json();
      return data.country;
    } catch (error) {
      console.error("Error detecting region:", error);
      return null;
    }
  },

  /**
   * Get the store name for display purposes.
   */
  getStoreName() {
    const platform = this.getNativePlatform();
    if (platform === "ios") return "App Store";
    if (platform === "android") return "Google Play";
    return "app store";
  },

  /**
   * Store name for a subscription's provider (not the current device). Used by
   * provider-aware copy where the device may differ from where the subscription
   * was bought (e.g. an Apple subscriber viewing the web app, where
   * getStoreName() would have no native platform to key off).
   */
  getStoreNameForProvider(provider) {
    if (provider === "apple") return "App Store";
    if (provider === "google") return "Google Play";
    return "app store";
  },

  /**
   * The IAP provider of the current device's store: "apple" on iOS, "google" on
   * Android, null on web. Lets provider-aware logic check whether an IAP
   * subscriber can complete an in-app upgrade on this device (the in-place
   * base-plan replacement only works on the store the subscription lives on).
   */
  getDeviceStoreProvider() {
    const platform = this.getNativePlatform();
    if (platform === "ios") return "apple";
    if (platform === "android") return "google";
    return null;
  },

  /**
   * Decide which upgrade affordance an active subscriber should see, so the UI
   * never offers a payment path the backend's dual-subscription guards reject.
   * Pure function of its inputs (no globals) so it is unit-testable.
   *
   * Only meaningful when the user is locked to a provider (has an active paid
   * subscription); free users pass currentProvider=null and keep the normal
   * region-based options (isLocked=false, no flags set).
   *
   * @param {object} opts
   * @param {null|"stripe"|"apple"|"google"} opts.currentProvider - active sub's provider, or null
   * @param {"stripe-only"|"stripe-preferred"|"iap-only"} opts.paymentStrategy
   * @param {boolean} opts.isNativeApp
   * @param {null|"apple"|"google"} opts.deviceStoreProvider - store of THIS device
   * @returns {{isLocked: boolean, showStripeUpgrade: boolean, showIapUpgrade: boolean, showGuide: boolean, guideKind: null|"stripe"|"iap"}}
   */
  resolveUpgradeProviderView({
    currentProvider,
    paymentStrategy,
    isNativeApp,
    deviceStoreProvider,
  }) {
    const isLocked = currentProvider != null;
    const isIapProvider =
      currentProvider === "apple" || currentProvider === "google";

    // Stripe upgrade is in-place (Subscription.modify) and only surfaced where
    // the app offers Stripe at all: web and native stripe-preferred regions.
    const showStripeUpgrade =
      currentProvider === "stripe" && paymentStrategy !== "iap-only";

    // An IAP base-plan upgrade must happen in the app, on the same store the
    // subscription lives on. Requires native + the device store to match.
    const showIapUpgrade =
      isIapProvider &&
      isNativeApp &&
      deviceStoreProvider === currentProvider;

    const showGuide = isLocked && !showStripeUpgrade && !showIapUpgrade;
    let guideKind = null;
    if (showGuide) {
      guideKind = currentProvider === "stripe" ? "stripe" : "iap";
    }

    return { isLocked, showStripeUpgrade, showIapUpgrade, showGuide, guideKind };
  },

  /**
   * Get the subscription management URL for an IAP subscription, chosen by the
   * subscription's provider (not the current device) and the current surface.
   * Apple uses the on-device App Store deep link only inside the iOS app; on the
   * web (and on a non-iOS native surface, e.g. an Apple subscriber signed into
   * the Android app) it uses Apple's browser-based account subscriptions page.
   * Google Play's URL works on every surface. Returns null for stripe/null/
   * unknown providers.
   * @param {null|"apple"|"google"|"stripe"} provider - the subscription provider
   * @returns {string|null}
   */
  getStoreManagementUrl(provider) {
    if (provider === "apple") {
      return this.getNativePlatform() === "ios"
        ? "itms-apps://apps.apple.com/account/subscriptions"
        : "https://account.apple.com/account/manage/section/subscriptions";
    }
    if (provider === "google") {
      return "https://play.google.com/store/account/subscriptions";
    }
    return null;
  },

  /**
   * Read the current App Store storefront (alpha-3, e.g. "USA") from StoreKit via
   * the native Storefront plugin and cache it in the reactive ref. Returns the
   * code, or null if unavailable (plugin missing, no store account, payment
   * queue not ready). Accessed via window.Capacitor.Plugins so this module stays
   * importable without a static @capacitor/core dependency.
   */
  async _queryIosStorefrontOnce() {
    if (!_storefrontPlugin) return null;
    try {
      const { countryCode } = await _storefrontPlugin.getCountryCode();
      // Always assign so a resume that no longer reports a storefront (signed
      // out of the App Store, restricted device) clears a previously-resolved
      // value and fails closed to iap-only, rather than leaving a stale "USA".
      iosStorefrontCountry.value = countryCode || null;
      return iosStorefrontCountry.value;
    } catch (error) {
      console.warn("Storefront lookup failed:", error);
      iosStorefrontCountry.value = null;
      return null;
    }
  },

  /**
   * Initialize iOS storefront detection at app launch (call from App.vue
   * mounted). Nil at cold launch is expected (payment queue not ready), so retry
   * with a short bounded backoff, then register a resume re-query that recovers a
   * still-nil launch AND refreshes on a mid-session Apple ID region switch. No-op
   * off native iOS.
   */
  async initIosStorefront({ attempts = 3, delayMs = 1000 } = {}) {
    if (this.getNativePlatform() !== "ios") return;
    const { registerPlugin } = await import("@capacitor/core");
    _storefrontPlugin = registerPlugin("Storefront");
    for (let i = 0; i < attempts; i++) {
      const resolved = await this._queryIosStorefrontOnce();
      if (resolved != null) break;
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    // Resume re-query — the cheap alternative to a full SKPaymentQueueDelegate
    // storefront-change subscription, which is overkill for a buy-button gate.
    const capacitorApp = registerPlugin("App");
    capacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) this._queryIosStorefrontOnce();
    });
  },

  /** Test seam: drive the module-scoped iOS storefront ref from unit tests. */
  _setIosStorefrontCountryForTest(value) {
    iosStorefrontCountry.value = value;
  },
};
